import { Link2, Loader2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { shortenAddress } from '@/lib/utils';

interface LinkSocialStepProps {
    providerName: string;
    /** The connected owner wallet, which approves the transaction. */
    ownerAddress: string;
    /** The Snappy account the login joins; null while it is being read. */
    accountWallet: string | null;
    isWorking: boolean;
    error: string | null;
    onLink: () => void;
}

/**
 * The social login a wallet-first user added from the funding step, linked to
 * the account they just created, inside the same dialog. The regular binding
 * dialog owns every other path to the same transaction.
 */
const LinkSocialStep: React.FC<LinkSocialStepProps> = ({
    providerName,
    ownerAddress,
    accountWallet,
    isWorking,
    error,
    onLink,
}) => (
    <div className="flex flex-col items-center px-6 pb-6 pt-2 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl border border-border bg-secondary/50">
            <Link2 size={30} className="text-primary" />
        </div>
        <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
            One more step
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
            Your {providerName} login is signed in, and it joins the Snappy
            account you just set up. You approve one Cronos transaction that
            adds the login to it. Nothing else about the account changes.
        </p>

        <div className="mt-6 w-full rounded-xl border border-border p-3.5 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Your account
            </p>
            <p
                className="mt-1 font-mono text-sm text-foreground [overflow-wrap:anywhere]"
                title={accountWallet ?? undefined}
            >
                {accountWallet ?? 'Reading your account from Cronos'}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                Owned by{' '}
                <span className="font-mono text-foreground" title={ownerAddress}>
                    {shortenAddress(ownerAddress)}
                </span>
                , the wallet you connected.
            </p>
        </div>

        {error && !isWorking && (
            <div className="mt-4 min-w-0 [overflow-wrap:anywhere]">
                <p role="alert" className="text-sm text-destructive">
                    {error}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Press Link {providerName} to try again.
                </p>
            </div>
        )}

        <Button
            size="lg"
            className="mt-6 w-full font-semibold"
            disabled={isWorking}
            onClick={onLink}
        >
            {isWorking && <Loader2 className="animate-spin" />}
            {isWorking
                ? 'Confirm the transaction in your wallet'
                : `Link ${providerName}`}
        </Button>
    </div>
);

export default LinkSocialStep;
